import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Input, Tag, Col, Row } from 'antd';
import { EyeOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import EntranceControlForm from '../LeadPolicy/LeadPolicyEditorForm';
import dayjs from 'dayjs';

const LeadPolicyEditorListAdmin = ({ eventId }) => {
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [formInitialData, setFormInitialData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Function for loading input controls
  const fetchEntranceControls = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/lead-policy/admin');
      console.log(response.data);
      setControls(response.data);
      setFilteredControls(response.data);
    } catch (error) {
      message.error('Error fetching policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntranceControls();
  }, []);

  const handleView = (record) => {
    setIsEditing(true);
    setFormInitialData(record);
    setFormVisible(true);
  };

  const handleDelete = async (record) => {
    setLoading(true);
    try {
      const { _id, isDeleted } = record; 
      const newDeletedState = !isDeleted;
  
      await axios.put(`/entrance-controls/admin/${_id}`, {
        isDeleted: newDeletedState,
      });

      message.success(`Policy ${newDeletedState ? 'marked as deleted' : 'restored'} successfully`);
      fetchEntranceControls();
    } catch (error) {
      console.error('Error toggling isDeleted state:', error);
      message.error('Error toggling isDeleted state');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setFormVisible(false); 
    setFormInitialData(null); 
  };


  const handleSearchControls = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = controls.filter((control) =>
      control.name.toLowerCase().includes(value) ||
      control.policyRules.toLowerCase().includes(value) ||
      dayjs(control.createdAt).format('DD-MM-YYYY').includes(value)
    );

    setFilteredControls(filtered);
  };

  const columns = [
    {
      title: 'Client',
      dataIndex: 'clientID',
      key: 'clientID',
      responsive: ['md', 'lg'],
      render: (text, record) => (
        record.event?.clientID ? (
          <Tag color={record.event.clientID.clientColor || 'default'}>
            {record.event.clientID.name}
          </Tag>
        ) : (
          <Tag color="red">N/A</Tag>
        )
      ),
      sorter: (a, b) => {
        const nameA = a.event?.clientID?.name?.toLowerCase() || '';
        const nameB = b.event?.clientID?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB); 
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB); 
      },
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (event) => (
        event ? (
          <Tag color={event.clientID.clientColor || 'default'}>
            {event.mnemonic}
          </Tag>
        ) : (
          <Tag color="red">N/A</Tag>
        )
      ),
      sorter: (a, b) => {
        const nameA = a.event?.mnemonic?.toLowerCase() || '';
        const nameB = b.event?.mnemonic?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md', 'lg'],
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (text) => dayjs(text).format('DD-MM-YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button 
            icon={record.isDeleted ? <DeleteOutlined style={{ color: 'red' }} /> : <DeleteOutlined />} 
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px'}}>
      <h1 style={{ marginBottom: '24px' }}>Lead Policy Editor</h1>

      <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={10} lg={8}>
          <Input
            placeholder="Search by name, policy rules, or creation date"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={handleSearchControls}
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredControls}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
      />

      {formVisible && (
        <EntranceControlForm
          visible={formVisible}
          onClose={handleCloseModal}
          fetchEntranceControls={fetchEntranceControls}
          initialData={formInitialData}
          isEditing={isEditing}
          adminMode
        />
      )}
    </div>
  );
};

export default LeadPolicyEditorListAdmin;